<template>
  <v-row class="mx-0 mt-0 mb-4 noAnimation">
    <v-col>
      <v-form
        v-model="isValidForm"
        ref="initOTPForm"
        @submit="isValidForm && initPayment()"
        onSubmit="return false;"
      >
        <Alert
          :message="error"
          type="error"
          v-if="showError"
          @toggleAlert="showError = !showError"
        />
        <v-row>
          <v-col class="py-0">
            <v-select
              color="success"
              v-model="selectedCountry"
              :rules="countryCodeRules"
              :items="groupedOperatorCountries"
              item-text="countryEnglishName"
              item-value="countryCode"
              :label="merchantConfig.texts.countriesDropDownLabel"
              @change="onChangeCountry()"
              return-object
              :loading="!dataFetched"
              :disabled="!dataFetched"
              :readonly="!merchantConfig.fields[0].isEditable"
            ></v-select>
          </v-col>
        </v-row>

        <v-row justify-space-between>
          <v-col cols="12" sm="6" class="py-0">
            <v-select
              :readonly="
                operators.length === 1 || !merchantConfig.fields[1].isEditable
              "
              color="success"
              v-model="selectedOperator"
              :rules="operatorCodeRules"
              :items="operators"
              item-text="englishName"
              item-value="operatorCode"
              :label="merchantConfig.texts.operatorsDropDownLabel"
              @change="onChangeOperator()"
              return-object
              :loading="!dataFetched"
              :disabled="!dataFetched"
            ></v-select>
          </v-col>

          <v-col cols="12" sm="6" class="py-0" v-if="showMobileNumber">
            <v-text-field
              :readonly="!merchantConfig.fields[2].isEditable"
              class="mobileNumber"
              color="success"
              v-model="mobileNumber"
              :rules="mobileNumberRules"
              :label="merchantConfig.texts.mobileNumberTextFieldLabel"
              :prefix="textFieldPrefix"
              maxlength="13"
              :validate-on-blur="validateMobileOnBlur"
              :loading="!dataFetched"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row v-if="merchantConfig.fields.length > 3">
          <template v-for="(field, index) in merchantConfig.fields.slice(3)">
            <v-col :key="index" cols="12" sm="6" class="py-0" v-if="field.show">
              <v-text-field
                :readonly="!field.isEditable"
                color="success"
                v-model="field.value"
                :label="
                  getAdditionalFieldLabel(field.key) +
                  (field.isMandatory ? ' *' : '')
                "
                :rules="[
                  (value) => {
                    if ((value == '' || value == null) && field.isMandatory) {
                      return $translate('requiredInput');
                    } else if (
                      value != '' &&
                      value != null &&
                      field.regex &&
                      !new RegExp(
                        field.regex.substring(1, field.regex.length - 1)
                      ).test(value)
                    ) {
                      return $translate('invalidInputFormat');
                    } else {
                      return true;
                    }
                  },
                ]"
              ></v-text-field>
            </v-col>
          </template>
        </v-row>

        <Alert
          v-if="showOperatorAlert"
          :message="operatorAlertMsg"
          type="warning"
        />
        <v-row>
          <v-col>
            <v-btn
              elevation="0"
              block
              :disabled="!isButtonActive"
              :loading="loading"
              :rounded="templateConfig.isButtonRounded"
              id="paymentBtn"
              class="mainButton"
              @click="initPayment()"
            >
              <span v-html="initBtnText" v-if="!loading"></span>
            </v-btn>
          </v-col>
        </v-row>

        <SecondaryDisclaimer
          v-if="
            vatDisclaimerMsg != 'NoDisclaimer' || initSecondaryDisclaimerText
          "
        >
          <div class="secondryDisc">
            <VatDisclaimerMsg :message="vatDisclaimerMsg" />
            <div v-html="initSecondaryDisclaimerText"></div>
          </div>
        </SecondaryDisclaimer>
      </v-form>
    </v-col>
  </v-row>
</template>

<script>
//1.General imports.
import { mapState, mapMutations } from 'vuex';
import { fraudToolMixin } from '../../Shared/Mixins/fraudToolMixin';
import simulateCodes from 'constants/simulateCodes';
import { SubmitForm } from 'helper/formHelper';
import mobileNumberRules from '../../Shared/Rules/mobileNumberRules';
import requiredRules from '../../Shared/Rules/requiredRules.js';
import {
  digitsConverter,
  removeExtraZero,
  getFormattedMsisdn,
} from '../../../helper/digitsHelper.js';

//2.shared comp. imports.
import { operatorPatternValidationMixin } from '../../Shared/Mixins/operatorPatternValidationMixin';
import VatDisclaimerMsg from '../../Shared/components/VATDisclaimerMsg.vue';
import SecondaryDisclaimer from '../../Shared/components/Disclaimer.vue';
import regex from '../../../constants/regex';
import saveUserDetails from '../../Shared/API/saveUserDetails';
import Alert from '../../Shared/components/Alert.vue';
import languageKeys from 'constants/languageKeys'; 

export default {
  mixins: [operatorPatternValidationMixin, fraudToolMixin],
  data() {
    return {
      loading: false,
      error: '',
      showError: false,
      operatorAlertMsg: '',
      showOperatorAlert: false,
      groupedOperatorCountries: [],
      operators: [],
      selectedCountry: null,
      selectedOperator: null,
      mobileNumber: '',
      textFieldPrefix: '',
      dataFetched: false,
      vatDisclaimerMsg: '',
      isValidForm: true,
      validateMobileOnBlur: false,
      countryCodeRules: requiredRules(this.$translate, 'COUNTRYREQUIRED'),
      operatorCodeRules: requiredRules(this.$translate, 'OPERATORREQUIRED'),
      saveUserDetails,
      sessionId: new Date().getTime().toString(),
    };
  },
  components: {
    VatDisclaimerMsg,
    SecondaryDisclaimer,
    Alert,
  },
  computed: {
    ...mapState(['templateConfig', 'merchantConfig', 'OTPDetails']),
    minMobileNumberLength() {
      return this.templateConfig.theme == 'Operator-Template' ? 9 : 8;
    },
    isButtonActive() {
      return this.isValidForm;
      },
    showMobileNumber() {
      return (
        this.selectedOperator == null ||
        (this.selectedOperator && !this.selectedOperator.handlesMsisdn)
      );
    },
    initBtnText() {
      //Needs $replacePlaceholders plugin function for [productName] placeholders.
      return this.$replacePlaceholders(this.merchantConfig.texts.initBtn, {
        productName: this.OTPDetails.productName,
      });
    },
    initSecondaryDisclaimerText() {
      return this.merchantConfig.texts.initSecondaryDisclaimer;
    },

    mobileNumberRules() {
      return mobileNumberRules(
        this.$translate,
        regex.MOBILE_NUMBER,
        this.merchantConfig?.texts.mobileNumberTextFieldLabel,
        this.minMobileNumberLength
      );
    },
  },
  watch: {
    mobileNumber() {
      if (
        this.mobileNumber &&
        this.mobileNumber.length < this.minMobileNumberLength &&
        regex.MOBILE_NUMBER.test(this.mobileNumber)
      ) {
        this.validateMobileOnBlur = true;
      } else {
        this.validateMobileOnBlur = false;
      }

      this.validateOperatorPattern();
    },
  },
  methods: {
    ...mapMutations(['setOTPDetails', 'setMerchantConfig']),
    async initPayment() {
      if (!this.$refs.initOTPForm.validate()) return;

      this.loading = true;
      this.showError = false;
      this.mobileNumber = digitsConverter(this.mobileNumber); //To convert from Hindi to Arabic numbers.
      this.mobileNumber = removeExtraZero(
        this.selectedCountry.countryCode,
        this.mobileNumber
      );

      //validateFraudTool (In fraudToolMixin).
      const validateFraudToolResult = await this.validateFraudTool(
        {
          operatorCode: this.selectedOperator.operatorCode,
          country: this.selectedCountry.twoLetterCountryCode,
          msisdn: this.textFieldPrefix.substring(1) + this.mobileNumber,
          //...this.selectedOperator.merchantAdditionalParams, //used for Evina in Sub only
        },
        this.sessionId
      );

      if (!validateFraudToolResult.isUserClear) {
        //moveToFailure step with fruadTool failure msg.
        this.setOTPDetails({
          step: 4,
          component: 'OTPFailure',
          failureMessage: this.$translate('FRAUDTOOLERRORMSG'),
          operationStatusCode: 102, // Note : 102 "operationStatusCode" === Fraud in the back-end.
        });
        return;
      }

      await this.saveUserDetails(
        this.selectedOperator.operatorCode,
        this.selectedCountry.countryCode + this.mobileNumber
      );

      //construct the addional fields object
      let CustomerContactInfo = {};
      let CustomParameters = [];
      if (this.merchantConfig.fields.length > 3) {
        this.merchantConfig.fields.slice(3).forEach((field) => {
          if (field.value) {
            let customParameter = {
              key: field.key,
              value: field.value,
            };
            CustomParameters.push(customParameter);
          }
          CustomerContactInfo[field.key] = field.value;
        });
      }
    if (this.selectedOperator.operatorVerificationType == 1) {
      if( Object.keys(CustomerContactInfo).length === 0)
         CustomerContactInfo=null;
        if (!this.OTPDetails.isDynamicPayment) {
          let redirectToOperatorRequest = {
            Msisdn: this.selectedCountry.countryCode + this.mobileNumber,
            OperatorCode: this.selectedOperator.operatorCode,
            OrderInfo: this.OTPDetails.orderInfo,
            Language: parseInt(Object.keys(languageKeys).find(key => languageKeys[key] === this.templateConfig.language)),
            CustomParameters,
            ProductId: this.OTPDetails.productId,
              ProductCatalogName: this.OTPDetails.productCatalogName,
            OverrideProductName: this.OTPDetails.overrideProductName,
            SessionToken: this.sessionId,
            RedirectURL: this.OTPDetails.operatorRedirectionRedirectBackUrl
          };
         window.TPay.OneTimePayment.redirectToOperator(redirectToOperatorRequest);
        }
        else {
          let redirectToOperatorPremiumRequest = {
            Msisdn: this.selectedCountry.countryCode + this.mobileNumber,
            OperatorCode: this.selectedOperator.operatorCode,
            OrderInfo: this.OTPDetails.orderInfo,
            Language: parseInt(Object.keys(languageKeys).find(key => languageKeys[key] === this.templateConfig.language)),
            CustomParameters,
            ProductPrice: this.OTPDetails.productPrice,
            ProductName: this.OTPDetails.productName,
            SessionToken: this.sessionId,
            RedirectURL: this.OTPDetails.operatorRedirectionRedirectBackUrl
          };
         window.TPay.OneTimePayment.redirectToOperatorPremium(redirectToOperatorPremiumRequest);
        }
        return;
    }

      let response = await this.$http.post('/otp/InitializeOneTimePayment', {
        Msisdn: this.selectedCountry.countryCode + this.mobileNumber,
        OperatorCode: this.selectedOperator.operatorCode,
        Simulate: process.env.NODE_ENV === 'development' ? true : false,
        SimulateVerificationMethod: simulateCodes.instructions, //['instructions' , 'pincode']
        IsHybridSolutionEnabled: this.merchantConfig.configs.isHybridPayment,
        CustomerContactInfo,
        CustomParameters,
      });
      /*========= response object Template ========== */
      // let response = {
      //   data: {
      //     responseCode: 10,
      //     statusCode: 1,
      //     transactionId: '21',
      //     verificationInstruction: '',
      //     verificationMethod: 2,
      //   },
      // };
      /*============================================= */

      // success
      if (response.data.statusCode === 1) {
        // instruction flow case
        //responseCode = 21 means pending responseStatus
        // USSD_PUSH = 7, USSD_PULL = 6, INSTRUCTIONS = 9, USSD_PUSH_With_Instructions = 10
        if (
          response.data.responseCode === 10 &&
          (response.data.verificationMethod === 6 ||
            response.data.verificationMethod === 7 ||
            response.data.verificationMethod == 9 ||
            response.data.verificationMethod === 10)
        ) {
          this.setOTPDetails({
            component: 'OTPInstructions',
            step: 3,
            otpDictionary: {
              instructions: response.data.verificationInstruction,
              msisdn: this.selectedCountry.countryCode + this.mobileNumber,
              operatorCode: this.selectedOperator.operatorCode,
              productPrice: this.selectedOperator.disclaimerParams.price,
              transactionId: response.data.transactionId,
              currency: this.selectedOperator.disclaimerParams.currency,
            },
          });
        }

        // redirection flow
        if (
          response.data.responseCode === 10 &&
          response.data.verificationMethod === 4
        ) {
          //consent verification
          SubmitForm(response.data.verificationInstruction);
          return;
        }
        // pincode flow
        else if (
          response.data.responseCode === 10 &&
          (response.data.verificationMethod === 0 ||
            response.data.verificationMethod === 2)
        ) {
          this.setOTPDetails({
            component: 'OTPPincode',
            step: 2,
            otpDictionary: {
              transactionId: response.data.transactionId,
              operatorCode: this.selectedOperator.operatorCode,
              msisdn: this.selectedCountry.countryCode + this.mobileNumber,
              productPrice: this.selectedOperator.disclaimerParams.price,
              currency: this.selectedOperator.disclaimerParams.currency,
            },
          });
        }
      } else if (response.data.statusCode === 0) {
        if (
          response.data.responseCode === 105 &&
          this.merchantConfig.fields[2].isEditable
        ) {
          this.error = this.$translate(
            `RESPONSECODE_${response.data.responseCode}`
          );
          this.showError = true;
          this.loading = false;
        } else {
          this.setOTPDetails({
            component: 'OTPFailure',
            step: 3,
            operationStatusCode: response.data.responseCode,
          });
        }
      } else if (response.data.statusCode === 3) {
        this.error = this.$translate(
          'RESPONSECODE_' + response.data.responseCode
        );
        this.showError = true;
        this.loading = false;
      }
    },
    async initializeFormData() {
      // 1.default case - select the matched operator
      for (let countryIndex in this.groupedOperatorCountries) {
        let country = this.groupedOperatorCountries[countryIndex];

        for (let operatorIndex in country.operatorInfoList) {
          let operator = country.operatorInfoList[operatorIndex];

          if (operator.operatorCode === this.OTPDetails.operatorCode) {
            this.selectedCountry = country;
            this.operators = country.operatorInfoList;
            this.selectedOperator = operator;
            await this.onChangeOperator();

            if (this.OTPDetails.msisdn) {
              this.mobileNumber = getFormattedMsisdn(
                this.OTPDetails.msisdn,
                this.selectedCountry.countryCode
              );
            }

            this.textFieldPrefix = '+' + this.selectedCountry.countryCode;
            return;
          }
        }
      }

      // 2.Select the first country otherwise
      if (this.groupedOperatorCountries.length == 1) {
        this.selectedCountry = this.groupedOperatorCountries[0];
        this.operators = this.groupedOperatorCountries[0].operatorInfoList;
        this.selectedOperator =
          this.groupedOperatorCountries[0].operatorInfoList[0];
        await this.onChangeOperator();

        if (this.OTPDetails.msisdn) {
          this.mobileNumber = getFormattedMsisdn(
            this.OTPDetails.msisdn,
            this.selectedCountry.countryCode
          );
        }
        this.textFieldPrefix = '+' + this.selectedCountry.countryCode;
      }

      // // special handling for DynamicPayment in case no matched operator
      // if (this.OTPDetails.isDynamicPayment) {
      //   if (!this.merchantConfig.fields[1].isEditable) {
      //     this.groupedOperatorCountries = [];
      //     this.operators = [];
      //   } else {
      //     this.selectedCountry = this.groupedOperatorCountries[0];
      //     this.setOTPDetails({ msisdn: '' });
      //   }
      // }
    },
    async onChangeCountry() {
      // set the first operator to be selected
      this.operators = this.selectedCountry.operatorInfoList;
      this.selectedOperator = this.operators[0];
      await this.onChangeOperator();
      this.textFieldPrefix = '+' + this.selectedCountry.countryCode;
    },
    getAdditionalFieldLabel(key) {
      //transform to camelCase and append "TextFieldLabel"
      key = key.replace(/^\w/, (c) => c.toLowerCase()) + 'TextFieldLabel';
      return this.merchantConfig.texts[key];
    },
    separateDCBFromWallets(dynamicResponse) {
      //paymentProviderType 1 DCB 2 Wallet
      let paymentProviderType = dynamicResponse.data.operatorInfoList.find(
        (operator) => operator.operatorCode === this.OTPDetails.operatorCode
      )?.paymentProviderType;

      //saving paymentProviderType in store to be used in OTP-failure step "google analytics".
      this.setOTPDetails({
        paymentProviderType: paymentProviderType == 1 ? 'DCB' : 'wallet',
      });

      dynamicResponse.data.operatorInfoList =
        dynamicResponse.data.operatorInfoList.filter(
          (operator) => operator.paymentProviderType === paymentProviderType
        );

      return dynamicResponse;
    },
    async onChangeOperator() {
      this.validateOperatorPattern();
      //update the available additional params in the store
      let tempMerchantConfig = this.merchantConfig;
      tempMerchantConfig.fields.forEach((elem) => {
        let key = elem.key;

        let matchedField = this.selectedOperator.additionalParams.find(
          (item) => item.key === key
        );

        if (matchedField === undefined) {
          elem.show = false;
        } else {
          elem.show = true;

          elem.isMandatory = matchedField.isMandatory;
          elem.regex = matchedField.regex;
          //override isEdiatable to be true in case a filed has no vlue
          if (
            (elem.value == undefined || elem.value == '') &&
            elem.isMandatory &&
            !elem.isEditable
          ) {
            elem.isEditable = true;
          }
        }
      });

      this.setMerchantConfig(tempMerchantConfig);

      //Storing [productPrice,currency] in store used in cardHeader.
      this.setOTPDetails({
        productPrice: this.selectedOperator.disclaimerParams.price,
        currency: this.selectedOperator.disclaimerParams.currency,
        selectedOperator: this.selectedOperator,
      });
        //injectFraudTool from "fraudToolMixin"
      await this.injectFraudTool(
        this.selectedOperator.fraudToolName,
        this.selectedOperator.fraudToolParameters,
          this.sessionId,
          this.selectedOperator.operatorCode,
          this.selectedCountry.twoLetterCountryCode
      );
      //Setting vatDiscalimer on every change in operators dropdown.
      this.vatDisclaimerMsg =
        this.selectedOperator.disclaimerParams.vatDisclaimer;
    },
  },

  async created() {
    await this.includeFraudPackage();
    let response;
    if (this.OTPDetails.isDynamicPayment) {
      response = await this.$http.get(
        `/merchant/operator/${this.OTPDetails.operatorCode}`
      );

      //separate DCB/Wallet
      if (this.merchantConfig.configs.SeparateDCBFromWallets) {
        response = this.separateDCBFromWallets(response);
      }
    } else {
      response = await this.$http.get(
        `/merchant/operatorproducts?catalogName=${
          this.OTPDetails.productCatalogName
        }${
          !this.merchantConfig.configs.IsMultipleProducts
            ? `&productSKU=` + this.OTPDetails.productId
            : ''
        }${
          !this.merchantConfig.fields[1].isEditable
            ? `&operatorCode=` + this.OTPDetails.operatorCode
            : ''
        }`
      );
    }

    if (response.data) {
      this.groupedOperatorCountries = Array.isArray(response.data)
        ? response.data
        : [response.data];
    } else {
      //in case Status Code: 204 "no grouped operator countries retruned from API"
      return;
    }

    await this.initializeFormData();
    this.dataFetched = true;
  },

  updated() {
    let formShouldValidate = this.merchantConfig.fields
      .slice(3)
      .find((field) => {
        return (
          field.show === true &&
          field.value &&
          field.isEditable &&
          !new RegExp(field.regex.substring(1, field.regex.length - 1)).test(
            field.value
          )
        );
      });
    if (formShouldValidate) {
      this.$refs.initOTPForm.validate();
    }
  },

  deactivated() {
    this.loading = false;
  },
};
</script>
